import type { DatabaseEngine } from "./types";

/**
 * The five database engines in words rather than in wire values.
 *
 * The ids are the API's and must not change. The labels are ours, and the
 * sentence under each is `create.json` under `engine`.
 * `port` and `backups` mirror the engine spec in maxlayer-api
 * (providers/dokploy/databases.ts); there is no build-time link between the
 * two, so a change there is a change here too.
 *
 * Shared by the create page, which renders the choice, and the list page,
 * which renders the label beside each running database.
 */
export const ENGINES: {
  id: DatabaseEngine;
  label: string;
  /** The port it listens on inside the project network. */
  port: number;
  /** False only for Redis — the deployment engine has no backup API for it. */
  backups: boolean;
  /**
   * Whether the engine has a named database, and whether it has a user. These
   * are two questions, not one: Redis has neither, but MongoDB has a user and
   * no named database — it creates one on first write. Collapsing them into a
   * single flag drew Mongo a "Database" row that could only ever be an em
   * dash. Mirrors `hasDatabaseName`/`hasUser` in the API's engine spec.
   */
  hasDatabaseName: boolean;
  hasUser: boolean;
  /** The command-line client, for the "how do I reach this" hint. */
  client: string;
  /** The variable name an attached connection string defaults to. */
  envKey: string;
}[] = [
  {
    id: "postgres",
    label: "PostgreSQL",
    port: 5432,
    backups: true,
    hasDatabaseName: true,
    hasUser: true,
    client: "psql",
    envKey: "DATABASE_URL",
  },
  {
    id: "mysql",
    label: "MySQL",
    port: 3306,
    backups: true,
    hasDatabaseName: true,
    hasUser: true,
    client: "mysql",
    envKey: "DATABASE_URL",
  },
  {
    id: "mariadb",
    label: "MariaDB",
    port: 3306,
    backups: true,
    hasDatabaseName: true,
    hasUser: true,
    client: "mariadb",
    envKey: "DATABASE_URL",
  },
  {
    id: "mongo",
    label: "MongoDB",
    port: 27017,
    backups: true,
    hasDatabaseName: false,
    hasUser: true,
    client: "mongosh",
    envKey: "DATABASE_URL",
  },
  {
    id: "redis",
    label: "Redis",
    port: 6379,
    backups: false,
    hasDatabaseName: false,
    hasUser: false,
    client: "redis-cli",
    envKey: "REDIS_URL",
  },
];

/** For read-only display, where a picker is not being rendered. */
export function describeEngine(value: DatabaseEngine): string {
  return ENGINES.find((option) => option.id === value)?.label ?? value;
}

/**
 * The whole spec for one engine, for screens that vary by it.
 *
 * Falls back to the relational shape for an id we do not recognise: a new
 * engine added to the API before it is added here should render a row too many
 * rather than silently hide a database's credentials.
 */
export function engineSpec(value: DatabaseEngine): (typeof ENGINES)[number] {
  return (
    ENGINES.find((option) => option.id === value) ?? {
      id: value,
      label: value,
      port: 0,
      backups: true,
      hasDatabaseName: true,
      hasUser: true,
      client: "",
      envKey: "DATABASE_URL",
    }
  );
}
